import { z } from "zod";
import {
  AssessmentInputMode,
  CoachPhase,
  CompetencySlug,
  ConversationRole,
  HtsAnswerClassification,
  HtsConsistencyFlagOverrideDecision,
  HtsFlagPriority,
  HtsInputType,
  HtsClassificationTopic,
  HtsReadinessBand,
  HtsReadinessPillarKey,
  HtsReadinessTask,
  HtsTopicKind,
  MAX_STRUCTURE_SCORE,
  MAX_VOICE_TURNS,
  MAX_ANSWER_MESSAGE_LENGTH,
  QuestionType,
  RealtimeControlFrameType,
  RealtimeSessionProfile,
  ScoreValue,
  VoiceClientEvent,
  VoiceSessionPhase,
} from "./constants";
import { LengthVerdict } from "./frameworks";

export const QuestionTypeSchema = z.nativeEnum(QuestionType);

export const AssessmentInputModeSchema = z.nativeEnum(AssessmentInputMode);

export const ConversationRoleSchema = z.nativeEnum(ConversationRole);

export const HtsReadinessTaskSchema = z.nativeEnum(HtsReadinessTask);

export const HtsInputTypeSchema = z.nativeEnum(HtsInputType);

export const HtsAnswerClassificationSchema = z.nativeEnum(HtsAnswerClassification);

export const HtsFlagPrioritySchema = z.nativeEnum(HtsFlagPriority);

export const HtsConsistencyFlagOverrideDecisionSchema = z.nativeEnum(HtsConsistencyFlagOverrideDecision);

export const HtsClassificationTopicSchema = z.nativeEnum(HtsClassificationTopic);

export const HtsTopicKindSchema = z.nativeEnum(HtsTopicKind);

export const HtsReadinessBandSchema = z.nativeEnum(HtsReadinessBand);

export const HtsReadinessPillarKeySchema = z.nativeEnum(HtsReadinessPillarKey);

// A question's own pillar membership — which Readiness pillar(s) its answer
// feeds. Independent of classificationTopic (which only decides whether the
// answer gets probed), so an informational Section A/C question scores a
// pillar without any AI reading it. Empty = not scored. Integrity is never
// listed: it's derived from Section B as a whole in htsReadinessScoring.ts.
export const HtsReadinessPillarsSchema = z.array(HtsReadinessPillarKeySchema);

// A REPEATABLE_GROUP question's sub-fields — scalar inputType only (no
// nesting). The candidate UI renders these as add/remove-row columns and
// compiles every row into one JSON-stringified answer; the backend never
// parses this structure, so validation here is purely about what the admin
// editor is allowed to author.
export const HtsGroupFieldSchema = z
  .object({
    key: z.string().min(1),
    label: z.string().trim().min(1),
    inputType: HtsInputTypeSchema.exclude([HtsInputType.REPEATABLE_GROUP]),
    options: z.array(z.string().trim().min(1)).optional(),
  })
  .refine(
    (field) => {
      const needsOptions = field.inputType === HtsInputType.SELECT || field.inputType === HtsInputType.CHECKBOXES;
      return !needsOptions || (field.options?.length ?? 0) > 0;
    },
    { message: "A Dropdown or Checkboxes row field requires at least one option", path: ["options"] }
  );
export type HtsGroupField = z.infer<typeof HtsGroupFieldSchema>;

export const HtsGroupFieldsSchema = z.array(HtsGroupFieldSchema).min(1);

export const CoachPhaseSchema = z.nativeEnum(CoachPhase);

export const CompetencySlugSchema = z.nativeEnum(CompetencySlug);

export const ScoreValueSchema = z
  .number()
  .int()
  .min(ScoreValue.Unsuitable)
  .max(ScoreValue.Exceptional);

// Dimension-based scoring

export const ScoringDimensionSchema = z.object({
  key: z.string(),
  label: z.string(),
  weight: z.number().positive(), // decimal, e.g. 0.20 for 20%
  anchors: z.object({
    0: z.string(),
    25: z.string(),
    50: z.string(),
    75: z.string(),
    100: z.string(),
  }),
  reviewTrigger: z
    .object({
      atOrBelow: z.number(), // fires mandatory review when dimension score ≤ atOrBelow
      reason: z.string(),
    })
    .optional(),
});
export type ScoringDimension = z.infer<typeof ScoringDimensionSchema>;

// Scoring config is type-scoped: all questions of the same type for a competency
// share the same dimensions, weights, anchors, and triggers. Question text itself
// lives in the database (see the `Question` model in packages/database) so admins
// can manage it without a code deploy.
export const QuestionTypeConfigSchema = z.object({
  dimensions: z.array(ScoringDimensionSchema),
  topIndicators: z.array(z.string()).optional(),
  scoringNotes: z.array(z.string()).optional(),
  warningSignIndicators: z.array(z.string()).optional(),
});
export type QuestionTypeConfig = z.infer<typeof QuestionTypeConfigSchema>;

export const CompetencySchema = z.object({
  id: CompetencySlugSchema,
  name: z.string(),
  definition: z.string(),
  questionTypes: z.record(QuestionTypeSchema, QuestionTypeConfigSchema),
});
export type Competency = z.infer<typeof CompetencySchema>;

export const TokenUsageSchema = z.object({
  promptTokens: z.number().int().min(0),
  completionTokens: z.number().int().min(0),
  totalTokens: z.number().int().min(0),
});
export type TokenUsage = z.infer<typeof TokenUsageSchema>;

export const RecordTokenUsageSchema = TokenUsageSchema.extend({
  userId: z.number().int().nullable().optional(),
});
export type RecordTokenUsage = z.infer<typeof RecordTokenUsageSchema>;

export const DimensionScoreSchema = z.object({
  key: z.string(),
  score: z.number().min(0).max(100),
  rationale: z.string(),
});
export type DimensionScore = z.infer<typeof DimensionScoreSchema>;

// Coach dimensions may be Not assessed (score: null); HTS readiness
// dimensions are always scored and keep using non-nullable
// DimensionScoreSchema so the HTS scorer can't silently emit null too.
export const CoachDimensionScoreSchema = DimensionScoreSchema.extend({
  score: z.number().min(0).max(100).nullable(),
});
export type CoachDimensionScore = z.infer<typeof CoachDimensionScoreSchema>;

// Same field shape as DimensionScoreSchema (key/score/rationale) — one entry
// per STARR/CARL framework component (see FrameworkComponentSchema in
// frameworks.ts) instead of per competency dimension. Scored 0–MAX_STRUCTURE_SCORE
// (not 0–100 like dimensions) so it shares the aggregate structureScore's scale
// and anchor vocabulary — components aren't weighted yet, so a 0–100 score would
// imply a precision/importance we haven't actually defined per component.
export const StructureComponentScoreSchema = z.object({
  key: z.string(),
  score: z.number().min(0).max(MAX_STRUCTURE_SCORE),
  rationale: z.string(),
  // Computed from `startMarker` (see withComputedWordCounts in
  // assessmentResponse.ts), never returned by the LLM directly. Optional for
  // historical rows persisted before this field existed.
  wordCount: z.number().int().min(0).optional(),
  // Real elapsed speaking time for this component, derived from voice-session
  // turn timings (see withComputedWordCounts). Present only for voice-mode
  // answers where timing data was available — absent for text answers, which
  // fall back to the wordCount-based estimate in the UI.
  actualDurationMs: z.number().int().min(0).optional(),
  // Code-computed length verdict (classifyAnswerLengths), never the LLM's.
  // Optional for rows persisted before it existed.
  lengthVerdict: z.nativeEnum(LengthVerdict).optional(),
});
export type StructureComponentScore = z.infer<typeof StructureComponentScoreSchema>;

/**
 * `DimensionScoreSchema` with `key` narrowed to exactly `dimensionKeys`, or
 * left as a plain string when the caller has no keys to constrain it to.
 *
 * `z.enum` needs a statically non-empty tuple; pulling the head off proves
 * that to the compiler, so no `as [string, ...string[]]` cast is needed —
 * the same technique createHtsConsistencyCheckResultSchema uses for question
 * ids further down.
 */
const dimensionScoreSchemaFor = (dimensionKeys: readonly string[]) => {
  const [firstKey, ...restKeys] = dimensionKeys;
  if (firstKey === undefined) return DimensionScoreSchema;
  return DimensionScoreSchema.extend({ key: z.enum([firstKey, ...restKeys]) });
};

/**
 * Coach-only variant of dimensionScoreSchemaFor, with `rationale` ordered
 * before `score` — structured-output generation fills fields in schema
 * order, so the model reasons through the evidence before scoring. Built
 * fresh because DimensionScoreSchema.extend() can't reorder keys.
 *
 * One shape per key, so only `notAssessedEligibleKeys` get `calledFor`
 * (between rationale and score). resolveNotAssessed picks which, if any,
 * becomes Not assessed.
 */
const coachDimensionScoreSchemaFor = (
  dimensionKeys: readonly string[],
  notAssessedEligibleKeys: readonly string[]
) => {
  const scoreSchema = z.number().min(0).max(100);
  const entryFor = (key: string) =>
    z.object({
      // Single-value enum rather than z.literal: `enum` is safely supported
      // by strict structured-output mode.
      key: z.enum([key]),
      rationale: z.string(),
      ...(notAssessedEligibleKeys.includes(key) ? { calledFor: z.boolean() } : {}),
      score: scoreSchema,
    });
  const [firstKey, secondKey, ...restKeys] = dimensionKeys;
  if (firstKey === undefined) {
    return z.object({ key: z.string(), rationale: z.string(), score: scoreSchema });
  }
  if (secondKey === undefined) return entryFor(firstKey);
  return z.union([entryFor(firstKey), entryFor(secondKey), ...restKeys.map(entryFor)]);
};

export const AgentScoreResultSchema = z.object({
  dimensionScores: z.array(CoachDimensionScoreSchema),
  // No structure headline field: computeStructureScore derives it from these.
  structureComponentScores: z.array(StructureComponentScoreSchema),
  // Short summary specific to the structure score — separate from the overall
  // `rationale` (which covers competency dimensions) and from the per-component
  // structureComponentScores[].rationale entries.
  structureRationale: z.string(),
  // Last: fields are generated in schema order, so the narrative follows the evidence.
  rationale: z.string(),
});
export type AgentScoreResult = z.infer<typeof AgentScoreResultSchema>;

// Constrains dimensionScores[].key and structureComponentScores[].key to the
// exact set of valid keys for one competency/question-type config and
// framework, so the model's structured output can't return a mangled or
// unknown key — enforced via the schema sent to the provider instead of
// fuzzy-matching the key after the fact.
//
// Not assessed = model's per-dimension `calledFor` + a code-applied cap
// (resolveNotAssessed). Strict structured output can't cap nulls, and a
// nullable score blew past the cap on most runs.
export const createAgentScoreResultSchema = (
  dimensionKeys: readonly string[],
  structureComponentKeys: readonly string[],
  // Keys that may be Not assessed — see isEligibleForNotAssessed.
  notAssessedEligibleKeys: readonly string[] = []
) =>
  AgentScoreResultSchema.extend({
    dimensionScores: z.array(coachDimensionScoreSchemaFor(dimensionKeys, notAssessedEligibleKeys)),
    structureComponentScores: z.array(
      // wordCount isn't requested from the model — it can't reliably
      // self-report (observed: undercounting a 444-word section as 211;
      // anchoring "majority of the time" components to ~40-50% regardless of
      // truth). actualDurationMs is excluded too, for a simpler reason: it's
      // derived purely from voice-session timestamps the model never sees.
      // Instead it gives `startMarker`, a verbatim quote of where the
      // component begins (empty if absent), which the caller resolves into
      // both fields deterministically (see withComputedWordCounts in
      // assessmentResponse.ts); lengthVerdict is computed from those. All
      // three stay omitted here despite being
      // `.optional()` on StructureComponentScoreSchema: Azure/OpenAI's strict
      // structured-output mode requires every `properties` key to also be
      // `required`, so a Zod-optional field left in is rejected outright.
      StructureComponentScoreSchema.omit({ wordCount: true, actualDurationMs: true, lengthVerdict: true }).extend({
        key:
          structureComponentKeys.length > 0
            ? z.enum(structureComponentKeys as [string, ...string[]])
            : StructureComponentScoreSchema.shape.key,
        startMarker: z.string().max(80),
      })
    ),
  });

type AgentScoreOutput = z.infer<ReturnType<typeof createAgentScoreResultSchema>>;

// Code-written so this wording only appears on the dimension
// resolveNotAssessed nulls.
export const notAssessedRationale = (label: string): string =>
  `The situation you described didn't give you a chance to show ${label}, so it wasn't scored for this answer.`;

/**
 * Of the `calledFor: false` dimensions, nulls only the lowest-weight one
 * (smallest renormalization shift) and gives it notAssessedRationale; the
 * rest keep their scores. Strips `calledFor` for downstream consumers.
 */
export const resolveNotAssessed = (
  result: AgentScoreOutput,
  dimensions: readonly Pick<ScoringDimension, "key" | "label" | "weight">[]
): Omit<AgentScoreOutput, "dimensionScores"> & { dimensionScores: CoachDimensionScore[] } => {
  const dimensionOf = (key: string) => dimensions.find((d) => d.key === key);
  const [notAssessedKey] = result.dimensionScores
    .filter((ds) => "calledFor" in ds && ds.calledFor === false)
    .map((ds) => ds.key)
    .sort((a, b) => (dimensionOf(a)?.weight ?? Infinity) - (dimensionOf(b)?.weight ?? Infinity));
  return {
    ...result,
    dimensionScores: result.dimensionScores.map(({ key, rationale, score }) =>
      key === notAssessedKey
        ? { key, rationale: notAssessedRationale(dimensionOf(key)?.label ?? key), score: null }
        : { key, rationale, score }
    ),
  };
};

// One candidate turn from a voice session, with wall-clock bounds (ms elapsed
// since the session started) — lets the backend measure real speaking time
// per framework component instead of estimating it from word count. Text is
// included so the backend can locate each component's startMarker within a
// specific turn rather than re-deriving turn boundaries from `message` alone.
export const VoiceTurnTimingSchema = z.object({
  text: z.string(),
  startedAtMs: z.number().int().min(0),
  endedAtMs: z.number().int().min(0),
});
export type VoiceTurnTiming = z.infer<typeof VoiceTurnTimingSchema>;

// Every candidate turn behind one submitted answer, in spoken order. Hoisted
// out of EvaluateRequestSchema so the route can validate the field on its own
// (see coachRequestValidation in routers/agents.ts) against the same
// definition the request type is built from.
export const VoiceTurnTimingsSchema = z.array(VoiceTurnTimingSchema).max(MAX_VOICE_TURNS);

export const EvaluateRequestSchema = z.object({
  message: z.string().min(1).max(MAX_ANSWER_MESSAGE_LENGTH),
  competencyId: CompetencySlugSchema,
  questionType: QuestionTypeSchema,
  threadId: z.string().optional(),
  questionText: z.string().optional(),
  assessmentId: z.string().optional(),
  // The InterviewTemplateItem being answered — omitted by the ad-hoc Evaluate
  // tool, which has none. Lets the backend recognise a resubmission instead of
  // writing a duplicate AssessmentResponse.
  interviewTemplateItemId: z.string().optional(),
  // Present only when `message` came from a voice session — see
  // VoiceTurnTimingsSchema.
  voiceTurnTimings: VoiceTurnTimingsSchema.optional(),
  // Which mode produced this answer — threaded through from the client's
  // request rather than inferred from voiceTurnTimings' presence, which stops
  // being a reliable signal once a voice answer can be evaluated after a mode
  // switch. Absent on the ad-hoc Evaluate tool.
  inputMode: AssessmentInputModeSchema.optional(),
});
export type EvaluateRequest = z.infer<typeof EvaluateRequestSchema>;

// Distinct from EvaluateRequestSchema (not an alias): Clarify never scores an
// answer, so it carries neither voiceTurnTimings nor inputMode. It shares
// Evaluate's optional assessmentId/interviewTemplateItemId, and for the same
// reason — the ad-hoc Evaluate tool (pages/Evaluate) chats to the coach with
// no sitting behind it at all. Naming the question is what upgrades the
// request to the deterministic per-question thread (so a text follow-up sees
// a voice-originated conversation); omitting it falls back to the caller's
// own threadId, which is all the ad-hoc tool has ever had.
export const ClarifyRequestSchema = z.object({
  message: z.string().min(1).max(MAX_ANSWER_MESSAGE_LENGTH),
  competencyId: CompetencySlugSchema,
  questionType: QuestionTypeSchema,
  threadId: z.string().optional(),
  questionText: z.string().optional(),
  assessmentId: z.string().optional(),
  interviewTemplateItemId: z.string().optional(),
});
export type ClarifyRequest = z.infer<typeof ClarifyRequestSchema>;

// WebRTC SDP negotiation for the realtime voice model (EVT-111). The browser
// posts its offer to our backend, which proxies it to Azure and returns the
// answer, so the Azure credentials never reach the client. The question is
// named rather than described — competency, question type and wording are all
// read from the InterviewTemplateItem server-side, so no part of the model's
// instructions can originate in the request body.
export const VoiceSessionRequestSchema = z.object({
  assessmentId: z.string().min(1),
  interviewTemplateItemId: z.string().min(1),
  offerSdp: z.string().min(1),
});
export type VoiceSessionRequest = z.infer<typeof VoiceSessionRequestSchema>;

// "This call ended" beacon (see VoiceService.endSession) — closes the voice
// observability span promptly instead of waiting out the length cap. The server
// hangs the call up and ignores a callId the caller doesn't own, so sending
// this early only ends your own session; it can't buy extra talk time.
export const VoiceSessionEndRequestSchema = z.object({
  callId: z.string().min(1),
});
export type VoiceSessionEndRequest = z.infer<typeof VoiceSessionEndRequestSchema>;

// Client-side health report for a live call (see VoiceService.reportEvent).
// Log-only on the server; ignored for a call someone else owns.
export const MAX_VOICE_CLIENT_EVENT_MESSAGE_LENGTH = 500;
export const VoiceSessionClientEventSchema = z.object({
  callId: z.string().min(1),
  event: z.nativeEnum(VoiceClientEvent),
  detail: z
    .object({
      state: z.string().max(MAX_VOICE_CLIENT_EVENT_MESSAGE_LENGTH).optional(),
      code: z.string().max(MAX_VOICE_CLIENT_EVENT_MESSAGE_LENGTH).optional(),
      message: z.string().max(MAX_VOICE_CLIENT_EVENT_MESSAGE_LENGTH).optional(),
    })
    .optional(),
});
export type VoiceSessionClientEvent = z.infer<typeof VoiceSessionClientEventSchema>;

// Which phase a voice session should open in. Discussing (the hands-free
// follow-up phase, never Answering again) whenever an AssessmentResponse
// already exists for this question, so a reconnect after any answer (voice or
// text) can never re-open the answer box. Asking — the coach reads the question
// aloud first — only on a genuinely fresh attempt, with no answer and nothing
// said yet; a reconnect mid-answer opens in Answering so the question is never
// read twice. See VoicePhase in useRealtimeVoice.ts.
export const VoiceSessionPhaseSchema = z.nativeEnum(VoiceSessionPhase);

// The `session.audio.input` block, always sent whole — session.updated's ack
// behavior for a partial audio.input object isn't documented. Only
// turn_detection's two response-gating flags vary by phase; everything else is
// fixed (see RealtimeSessionProfile / buildAudioInput).
export const RealtimeAudioInputSchema = z.object({
  transcription: z.object({ model: z.string().min(1) }),
  turn_detection: z.object({
    type: z.literal(RealtimeSessionProfile.TurnDetectionType),
    eagerness: z.literal(RealtimeSessionProfile.VadEagerness),
    create_response: z.boolean(),
    interrupt_response: z.boolean(),
  }),
  noise_reduction: z.object({ type: z.literal(RealtimeSessionProfile.NoiseReduction) }),
});
export type RealtimeAudioInput = z.infer<typeof RealtimeAudioInputSchema>;

// A full, server-authored frame the client echoes verbatim over the data
// channel. The client never assembles one — session.update's partial-merge
// semantics aren't documented (see buildControls in azureRealtimeSession.ts).
// Both shapes are still modelled here so the wire contract is checkable on
// either side.
//
// response.create deliberately carries no `response` object at all. It used to
// carry `response.instructions` set to the session's own instructions verbatim,
// because that field REPLACES the session instructions for a single response —
// so a short nudge string would have silently dropped every guardrail. Omitting
// the field entirely reaches the same place without the round trip: the
// Realtime API falls back to the session instructions when it isn't set. That
// also keeps the prompt (rubric indicators, guardrails, and the candidate's own
// previous attempt and score) server-side, where the browser can't read it out
// of the /voice/session response.
export const RealtimeControlFrameSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal(RealtimeControlFrameType.SessionUpdate),
    session: z.object({
      type: z.literal(RealtimeSessionProfile.SessionType),
      audio: z.object({ input: RealtimeAudioInputSchema }),
    }),
  }),
  z.object({
    type: z.literal(RealtimeControlFrameType.ResponseCreate),
  }),
]);
export type RealtimeControlFrame = z.infer<typeof RealtimeControlFrameSchema>;

export const VoiceSessionResponseSchema = z.object({
  answerSdp: z.string().min(1),
  initialPhase: VoiceSessionPhaseSchema,
  controls: z.object({
    answeringPhase: RealtimeControlFrameSchema,
    discussingPhase: RealtimeControlFrameSchema,
    feedbackNudge: RealtimeControlFrameSchema,
    askQuestion: RealtimeControlFrameSchema,
  }),
  // Azure's call identifier (captured server-side from the negotiate response)
  // plus the cap itself, so the client can name the call in its end-of-session
  // beacon and wind down gracefully before the server's hard stop. callId is
  // optional because a missing or reshaped `Location` header must never fail
  // the call itself — that one connection just loses its beacon, its wind-down,
  // and, with no handle to hang up by, its cap. Rare, logged, and accepted.
  callId: z.string().min(1).optional(),
  maxSessionDurationMs: z.number().int().positive(),
});
export type VoiceSessionResponse = z.infer<typeof VoiceSessionResponseSchema>;

// One finalized turn in a question's unified conversation — voice or text,
// candidate or coach. clientTurnId is generated once by whichever client
// (browser tab) produced the turn and reused verbatim on every flush retry,
// so a debounced/forced re-flush upserts the same Mastra message instead of
// duplicating it. It is an id within its own conversation, not a global one —
// the server namespaces it under the thread before storing (see
// questionConversation.ts's toMessageId/appendConversationTurns).
export const ConversationTurnSchema = z.object({
  clientTurnId: z.string().min(1),
  role: ConversationRoleSchema,
  text: z.string(),
  // Client wall-clock time the turn was finalized — carried through so
  // voice and text turns interleave in true chronological order across
  // however many mode switches happened, not insertion order.
  occurredAt: z.coerce.date(),
});
export type ConversationTurn = z.infer<typeof ConversationTurnSchema>;

export const AppendTurnsRequestSchema = z.object({
  turns: z.array(ConversationTurnSchema).min(1).max(MAX_VOICE_TURNS),
});
export type AppendTurnsRequest = z.infer<typeof AppendTurnsRequestSchema>;

// One question a sitting has its own answer on record for — what resuming an
// in-progress sitting needs to land on the right question and lock the rest
// (see the web app's useEnterAssessment). Deliberately carries no score,
// rationale, or review flag, so it can bypass the results gate in
// assessment.ts and go to every user class, guests included: it's the
// candidate's own input, not a withheld result.
export const AnsweredQuestionSchema = z.object({
  interviewTemplateItemId: z.string(),
  answer: z.string(),
});
export type AnsweredQuestion = z.infer<typeof AnsweredQuestionSchema>;

export const AssessmentResponseSchema = z.object({
  id: z.string(),
  competencyId: z.string(),
  questionType: QuestionTypeSchema,
  questionText: z.string().nullable(),
  answer: z.string(),
  rationale: z.string(),
  threadId: z.string().nullable(),
  createdAt: z.coerce.date(),
  dimensionScores: z.array(CoachDimensionScoreSchema).nullable().optional(),
  structureScore: z.number().nullable().optional(),
  structureRationale: z.string().nullable().optional(),
  structureComponentScores: z.array(StructureComponentScoreSchema).nullable().optional(),
  competencyScore: z.number().nullable().optional(),
  overallScore: z.number().nullable().optional(),
  reviewFlagged: z.boolean().optional(),
  reviewReasons: z.array(z.string()).optional(),
  // Which InterviewTemplateItem this answers — null for the ad-hoc Evaluate
  // tool. Lets the retake flow match a prior response back to its question.
  interviewTemplateItemId: z.string().nullable().optional(),
  // This item's most recent prior attempt's score (any earlier assessment,
  // full or partial retry) and the delta against it — null when there's no
  // prior attempt. See getResponseHistory in the API.
  previousOverallScore: z.number().nullable().optional(),
  scoreDelta: z.number().nullable().optional(),
  // Whether this answer is essentially the same as that previous attempt
  // (word-level near-identity, not just exact equality — a typo or one
  // swapped word still counts) — null when there's no prior attempt.
  // Scoring isn't perfectly deterministic, so a near-identical answer can
  // still show a small nonzero scoreDelta; the UI should prefer this flag
  // over the numeric delta when it's true, rather than surface score jitter
  // as if it were real change.
  sameAsPreviousAnswer: z.boolean().nullable().optional(),
  // True when this row wasn't scored by the assessment it's attached to at
  // all — it's the candidate's latest attempt at this question from an
  // earlier sitting, carried into a partial retake because the retake
  // didn't redo this particular question. See getCarriedOverResponses in
  // the API. Absent/false for a response the assessment actually scored.
  isCarriedOver: z.boolean().optional(),
  // Which earlier Assessment this carried-over row came from — only set
  // when isCarriedOver is true.
  fromAssessmentId: z.string().optional(),
  // True when this response's question differs from its immediately previous
  // attempt's; null when there's no previous attempt to compare. Drives the
  // results page's "different question" note and forces sameAsPreviousAnswer to null.
  isDifferentQuestion: z.boolean().nullable().optional(),
  inputMode: AssessmentInputModeSchema.nullable().optional(),
  // This answer's timing as an <answer_length> block, for a live voice
  // session to add once scoring lands. Null when coaching is off.
  answerLengthContext: z.string().nullable().optional(),
});
export type AssessmentResponse = z.infer<typeof AssessmentResponseSchema>;

/**
 * Where the candidate stands on one template question as of their latest
 * attempt at it in any sitting — see getLatestItemScores in the API. Sent
 * alongside the fetch-by-id assessment so the retake picker can rank the
 * whole template, including questions the sitting on screen didn't cover.
 */
export const ItemScoreHistoryEntrySchema = z.object({
  interviewTemplateItemId: z.string(),
  overallScore: z.number().nullable(),
  // The text of this item's most recent answered attempt anywhere in the
  // template — a prior retry may have substituted it, so this isn't always
  // the frozen questionText.
  lastAskedText: z.string(),
});
export type ItemScoreHistoryEntry = z.infer<typeof ItemScoreHistoryEntrySchema>;

/**
 * One entry of Assessment.itemSubstitutions, keyed by interviewTemplateItemId
 * — the substitution recorded for one retried item. questionId is
 * informational only (no FK, same convention as
 * InterviewTemplateItem.sourceQuestionId): the bank may since have changed.
 */
export const ItemSubstitutionSchema = z.object({
  questionId: z.string().nullable(),
  questionText: z.string(),
  // Whether this entry actually moved the candidate off their last-answered
  // question. False for a same-question pin (still recorded, to hold an
  // earlier sitting's substitute) and for a dead end — an entry's presence
  // alone never means "changed".
  changed: z.boolean(),
});
export type ItemSubstitution = z.infer<typeof ItemSubstitutionSchema>;

export const ItemSubstitutionsMapSchema = z.record(z.string(), ItemSubstitutionSchema);
export type ItemSubstitutionsMap = z.infer<typeof ItemSubstitutionsMapSchema>;

/**
 * One attempt's overall score, for the results-page score-trend chart. Mirrors
 * `getPreviousSittingTotalScores`'s sittings but includes the current one and
 * its id — see `getAssessmentScoreTrend` in the API.
 */
export const AssessmentOverallTrendAttemptSchema = z.object({
  assessmentId: z.string(),
  // This sitting's place in the candidate's full history of the template, not
  // its index in the window — past MAX_ATTEMPT_HISTORY sittings the window
  // slides, and the chart must still label them #5–#14, not renumber to #1–#10.
  attemptNumber: z.number().int().min(1),
  createdAt: z.coerce.date(),
  totalScore: z.number().nullable(),
  // Averaged across every template question's own competencyScore/structureScore
  // for this attempt (own answer or carried-over) — the two inputs
  // SCORE_BLEND_CONFIG blends into totalScore, plotted alongside it so their
  // movement is visible too. structureScore is normalized from its native
  // 0..MAX_STRUCTURE_SCORE scale to 0-100 to share the same axis.
  competencyScore: z.number().nullable(),
  structureScore: z.number().nullable(),
});
export type AssessmentOverallTrendAttempt = z.infer<typeof AssessmentOverallTrendAttemptSchema>;

// Trend attempts only ever chart a dimension's score, never its written
// rationale — carrying rationale here would multiply that text across every
// dimension, every question, and up to MAX_ATTEMPT_HISTORY historical
// attempts, for a payload the chart never reads it from.
export const AssessmentTrendDimensionScoreSchema = CoachDimensionScoreSchema.omit({ rationale: true });
export type AssessmentTrendDimensionScore = z.infer<typeof AssessmentTrendDimensionScoreSchema>;

/**
 * One attempt's data for a single template question, in the score-trend
 * chart — either that attempt's own answer, or (isCarriedOver: true) the
 * candidate's latest prior answer backfilled in because this attempt didn't
 * redo the question. Null score/dimensionScores means the question had no
 * answer at all yet as of this attempt.
 */
export const AssessmentItemTrendAttemptSchema = z.object({
  assessmentId: z.string(),
  /** Same numbering as the overall series — see AssessmentOverallTrendAttempt. */
  attemptNumber: z.number().int().min(1),
  createdAt: z.coerce.date(),
  overallScore: z.number().nullable(),
  dimensionScores: z.array(AssessmentTrendDimensionScoreSchema).nullable(),
  isCarriedOver: z.boolean(),
});
export type AssessmentItemTrendAttempt = z.infer<typeof AssessmentItemTrendAttemptSchema>;

/** One template question's score-trend series, oldest attempt first. */
export const ItemScoreTrendSchema = z.object({
  interviewTemplateItemId: z.string(),
  questionText: z.string(),
  competencyId: z.string(),
  questionType: QuestionTypeSchema,
  attempts: z.array(AssessmentItemTrendAttemptSchema),
});
export type ItemScoreTrend = z.infer<typeof ItemScoreTrendSchema>;

/**
 * The results page's score-trend chart data — up to MAX_ATTEMPT_HISTORY
 * attempts of the same template (this one plus prior ones, oldest first),
 * covering the overall score and every question's score + dimensions. See
 * `getAssessmentScoreTrend` in the API.
 */
export const AssessmentScoreTrendSchema = z.object({
  overall: z.array(AssessmentOverallTrendAttemptSchema),
  items: z.array(ItemScoreTrendSchema),
});
export type AssessmentScoreTrend = z.infer<typeof AssessmentScoreTrendSchema>;

// One condition for a REPEATABLE_GROUP question's hard gate: a row sub-field
// match (groupFieldKey/equals), or noneSelected: true for zero rows (the
// candidate picked "None"). Mutually exclusive.
export const HtsHardGateGroupConditionSchema = z
  .object({
    groupFieldKey: z.string().min(1).optional(),
    equals: z.union([z.string(), z.array(z.string())]).optional(),
    noneSelected: z.literal(true).optional(),
  })
  .refine(
    (condition) =>
      condition.noneSelected === true
        ? condition.groupFieldKey === undefined && condition.equals === undefined
        : condition.groupFieldKey !== undefined && condition.equals !== undefined,
    {
      message: "A group field condition needs either groupFieldKey + equals, or noneSelected: true, not both",
    }
  );
export type HtsHardGateGroupCondition = z.infer<typeof HtsHardGateGroupConditionSchema>;

// A deterministic, non-AI eligibility gate on a question's own answer — e.g.
// "not a citizen/PR" or "no driver's licence" — flag immediately if the
// answer equals a specific value, no LLM call involved. Mutually exclusive
// with isSensitive and classificationTopic (enforced in
// validateHtsQuestionInputs, not a DB constraint) — a question is either
// AI-classified, sensitive-carve-out, hard-gated, or purely informational,
// never more than one. See apps/api/src/services/htsHardGateCheck.ts.
export const HtsHardGateRuleSchema = z.object({
  // Required unless groupFieldConditions is set (REPEATABLE_GROUP questions
  // use only groupFieldConditions, each with its own `equals`).
  equals: z.union([z.string(), z.array(z.string())]).optional(),
  priority: HtsFlagPrioritySchema,
  reason: z.string(),
  // REPEATABLE_GROUP only: row sub-field conditions, OR'd together — flags if
  // ANY row's value for ANY listed sub-field matches its `equals`. Needed since
  // that question's answer is an array of rows, not a single scalar value.
  groupFieldConditions: z.array(HtsHardGateGroupConditionSchema).min(1).optional(),
});
export type HtsHardGateRule = z.infer<typeof HtsHardGateRuleSchema>;

// The shape of an HtsAnswer's decrypted value. Validating against this at
// decrypt time (rather than trusting the cipher's generic return type) is
// what guarantees `matchesHtsHardGateRule` and every downstream consumer
// actually gets a string | string[], not an unvalidated unknown.
export const HtsAnswerValueSchema = z.union([z.string(), z.array(z.string())]);
export type HtsAnswerValue = z.infer<typeof HtsAnswerValueSchema>;

// One HTS classification topic's classify/follow-up/flag config — the HTS
// analogue of CompetencySchema above, rendered into the htsSuitabilityClassifier
// agent's dynamic instructions (see packages/shared/src/htsClassificationTopics/*.ts
// and apps/api/src/mastra/agents/htsSuitabilityClassifier.ts).
export const HtsClassificationTopicConfigSchema = z.object({
  id: HtsClassificationTopicSchema,
  label: z.string(),
  // SUITABILITY (default, for every pre-existing Section B topic + GENERAL)
  // drives htsSuitabilityClassifier/htsSuitabilityFollowUp.ts. READINESS
  // drives htsReadinessFollowUp.ts instead — never both, and never the
  // suitability engine at all for a READINESS-kind question (see
  // htsAnswerChecks.ts's dispatch and isSuitabilityTopic/isReadinessTopic
  // below).
  kind: HtsTopicKindSchema.default(HtsTopicKind.Suitability),
  // What a *complete* disclosure/answer covers — drives what the follow-up
  // asks about, and what "already fully answered, no follow-up needed" looks
  // like. Used by both the suitability and the readiness follow-up engines.
  narrativeFields: z.array(z.string()),
  // When a follow-up is warranted despite a "yes"/substantive answer already
  // being logged.
  followUpTriggers: z.array(z.string()),
  // Facts that force classification=UNSUITABLE regardless of how well the
  // narrative resolves — instructions to the model, not code-level checks
  // (the one universal code-level rule lives in htsSuitabilityFollowUp.ts).
  // Not applicable to READINESS topics — always empty there.
  hardBarrierFacts: z.array(z.string()).default([]),
  // Topic-specific cross-cutting nuance (e.g. impaired driving's
  // recency-based escalation) that doesn't fit narrativeFields/hardBarrierFacts.
  crossCuttingNotes: z.array(z.string()).optional(),
  // Default 'neutral-summary'. Topics whose doc guidance calls for exact
  // wording in the flag note (e.g. protection-order/family-violence
  // disclosures) set 'verbatim-quote'.
  noteStyle: z.enum(["neutral-summary", "verbatim-quote"]).optional(),
});
export type HtsClassificationTopicConfig = z.infer<typeof HtsClassificationTopicConfigSchema>;

// One Readiness Score pillar's dimension weights + 0/25/50/75/100 behavioural
// anchors (Evaltick Readiness Scoring Rubric) — reuses ScoringDimensionSchema
// (the same shape already used for the competency rubrics' dimensions) rather
// than inventing a parallel one. `weight` here is the dimension's share of
// *this pillar* (e.g. 0.35), unrelated to `compositeWeight` below.
export const HtsReadinessPillarConfigSchema = z.object({
  key: HtsReadinessPillarKeySchema,
  label: z.string(),
  definition: z.string(),
  dataSourceNote: z.string(),
  dimensions: z.array(ScoringDimensionSchema),
  topIndicators: z.array(z.string()).optional(),
  scoringNotes: z.array(z.string()).optional(),
  // This pillar's share of the composite score, relative to a normal
  // pillar's implicit 1 — see computeCompositeScore. Optional rather than
  // `.default(1)` since most pillar configs are typed literals, not parsed
  // through this schema; every reader falls back to `?? 1`.
  compositeWeight: z.number().optional(),
});
export type HtsReadinessPillarConfig = z.infer<typeof HtsReadinessPillarConfigSchema>;

// Structured output for the per-question htsReadinessFollowUp agent —
// intentionally simpler than HtsSuitabilityFollowUpResultSchema below: there's
// no classification bucket to produce, just "is this answer thin/vague for
// scoring purposes, and if so what's a good gentle follow-up question."
//
// `crossReferenceCheck`: free-text chain-of-thought where the model checks
// each earlier answer in the questionnaire for the same underlying
// situation, before deciding needsFollowUp — so candidates aren't asked to
// repeat themselves. No separate code-gated field for this:
// HtsSuitabilityFollowUpResultSchema's `classification` below relies on
// model judgment alone with no gate either, so this matches.
export const HtsReadinessFollowUpResultSchema = z.object({
  crossReferenceCheck: z.string(),
  needsFollowUp: z.boolean(),
  followUpQuestion: z.string().nullable(),
});
export type HtsReadinessFollowUpResult = z.infer<typeof HtsReadinessFollowUpResultSchema>;

/**
 * Structured output for the submission-wide htsReadinessScorer agent — one
 * entry per pillar the model is actually asked to score. Built dynamically so
 * the model can neither hallucinate a pillar with no data nor drift a
 * dimension key, mirroring
 * createAgentScoreResultSchema/createHtsConsistencyCheckResultSchema below.
 *
 * `pillars` — the exact set of pillars this call scores, each with the exact
 * set of dimension keys it's allowed to return. For Integrity this is only
 * `["insightAccountability"]` (Conduct History / Honesty & Completeness are
 * computed deterministically in code from existing suitability data — see
 * htsReadinessScoring.ts) — every other present pillar passes all of its
 * config's dimension keys.
 *
 * `credibilityConcerns` is submission-wide, not per pillar: one entry per
 * answer the scorer set aside as not credible (one answer can feed several
 * pillars, and the reviewer reads the concern next to that answer).
 * `dimensionKeys` names the dimensions that entry would otherwise have fed —
 * code reads it to apply the Education Attainment cap.
 *
 * `rationale` mirrors `AgentScoreResultSchema.rationale` (the interview
 * coach's overall per-response narrative) — one pillar-level summary in
 * addition to each dimension's own `rationale`. For Integrity this only ever
 * narrates the one dimension the model was actually given
 * (`insightAccountability`); htsReadinessScoring.ts composes Integrity's
 * pillar-level rationale itself from all three dimensions rather than using
 * this field directly, since the other two are scored deterministically and
 * never reach the model at all.
 */
export const createHtsReadinessScorerResultSchema = (
  pillars: ReadonlyArray<{ key: string; dimensionKeys: readonly string[] }>,
  questionIds: readonly string[] = []
) => {
  const shape = Object.fromEntries(
    pillars.map(({ key, dimensionKeys }) => [
      key,
      z.object({
        rationale: z.string(),
        dimensionScores: z.array(dimensionScoreSchemaFor(dimensionKeys)),
      }),
    ])
  );
  // Constrained to the ids actually sent, the same way
  // createHtsConsistencyCheckResultSchema constrains its questionIds.
  const [firstQuestionId, ...restQuestionIds] = questionIds;
  const questionId = firstQuestionId !== undefined ? z.enum([firstQuestionId, ...restQuestionIds]) : z.string();
  const [firstDimensionKey, ...restDimensionKeys] = [...new Set(pillars.flatMap((p) => p.dimensionKeys))];
  const dimensionKey =
    firstDimensionKey !== undefined ? z.enum([firstDimensionKey, ...restDimensionKeys]) : z.string();
  return z.object({
    pillars: z.object(shape),
    credibilityConcerns: z.array(z.object({ questionId, dimensionKeys: z.array(dimensionKey), note: z.string() })),
  });
};

/**
 * Structured output for the second, submission-wide htsReadinessScorer call —
 * the narrative "Readiness Summary" itself (Evaltick Readiness Scoring
 * Rubric's own worked example), drafted only once every pillar's final score
 * is known (composite/band are computed deterministically in code, never by
 * the model — see htsReadinessScoring.ts — so this call necessarily runs
 * after, and separately from, createHtsReadinessScorerResultSchema's call).
 *
 * Two distinct audiences, per the doc: `coachSummary` may reference pillar
 * names/scores freely (evaluator-facing); `candidateSummary` mirrors the
 * doc's "Summary as the candidate sees it" structure — numbers-free prose in
 * three parts, matching its own worked example's headings.
 */
export const HtsReadinessNarrativeResultSchema = z.object({
  coachSummary: z.string(),
  candidateSummary: z.object({
    strengths: z.string(),
    priorities: z.string(),
    bottomLine: z.string(),
  }),
});
export type HtsReadinessNarrativeResult = z.infer<typeof HtsReadinessNarrativeResultSchema>;

/**
 * Structured output for sessionSummaryAgent — the end-of-session
 * write-up shown on SessionResults: a short holistic summary of the whole
 * sitting. Per-question comparison to a prior attempt is written live into
 * each AssessmentResponse's own rationale by interviewCoach at evaluate time
 * (see requestContext's PreviousAttempt), so this agent only needs to
 * synthesize across the (already comparison-aware) responses it's given.
 */
export const SessionSummaryResultSchema = z.object({
  overallSummary: z.string(),
});
export type SessionSummaryResult = z.infer<typeof SessionSummaryResultSchema>;

// Structured output for the per-question htsSuitabilityClassifier agent.
// `followUpQuestion` is nullable rather than optional — Azure OpenAI's
// strict structured-output mode requires every key in `properties` to also
// appear in `required`; expressing "not applicable" via `.optional()`
// (omitting the key) is rejected, so the model must always emit the key and
// use `null` when there's no follow-up question.
//
// `crossReferenceCheck`: free-text chain-of-thought where the model checks
// each earlier answer in the questionnaire for the same underlying
// situation, before classifying or deciding needsFollowUp — so candidates
// aren't marked incomplete, or asked to repeat themselves, over something
// already on record elsewhere. No separate code-gated field for this:
// `classification` below is also decided on model judgment alone, no gate.
export const HtsSuitabilityFollowUpResultSchema = z.object({
  crossReferenceCheck: z.string(),
  classification: HtsAnswerClassificationSchema,
  needsFollowUp: z.boolean(),
  followUpQuestion: z.string().nullable(),
  reviewReasons: z.array(z.string()),
});
export type HtsSuitabilityFollowUpResult = z.infer<typeof HtsSuitabilityFollowUpResultSchema>;

// Structured output for the submission-wide htsConsistencyChecker agent.
// Note-first: `note` states what the answers say, `mechanism` why both can't
// be true, and `isGenuineContradiction` is the verdict on those two — filtered
// on in code (runHtsConsistencyCheck), since the model sometimes drafts a flag
// its own verdict rejects. Only `note` is persisted.
const HtsConsistencyFlagSchema = z.object({
  questionIds: z.array(z.string()),
  note: z.string(),
  mechanism: z.string(),
  isGenuineContradiction: z.boolean(),
});

export const HtsConsistencyCheckResultSchema = z.object({
  flags: z.array(HtsConsistencyFlagSchema),
});
export type HtsConsistencyCheckResult = z.infer<typeof HtsConsistencyCheckResultSchema>;

// Constrains flags[].questionIds to the exact set of question ids present in
// this submission's transcript, so the model's structured output can't
// hallucinate or mangle one of these opaque id strings — enforced via the
// schema sent to the provider, the same fix already applied to
// createAgentScoreResultSchema's dimension keys.
export const createHtsConsistencyCheckResultSchema = (validQuestionIds: readonly string[]) => {
  // `z.enum` needs a statically non-empty tuple; pulling the head off proves
  // that to the compiler, so no `as [string, ...string[]]` cast is needed.
  const [firstQuestionId, ...restQuestionIds] = validQuestionIds;

  return HtsConsistencyCheckResultSchema.extend({
    flags: z.array(
      HtsConsistencyFlagSchema.extend({
        questionIds:
          firstQuestionId !== undefined
            ? z.array(z.enum([firstQuestionId, ...restQuestionIds]))
            : HtsConsistencyFlagSchema.shape.questionIds,
      })
    ),
  });
};

// The real limit is one follow-up per answer, enforced by the service; the 500 cap is an abuse backstop.
export const MAX_ADMIN_FOLLOW_UPS = 500;

export const SendHtsAdminFollowUpsRequestSchema = z.object({
  // An empty list means "no follow-ups needed".
  followUps: z
    .array(
      z.object({
        questionId: z.string().min(1),
        question: z.string().trim().min(1).max(MAX_ANSWER_MESSAGE_LENGTH),
      })
    )
    .max(MAX_ADMIN_FOLLOW_UPS),
});
export type SendHtsAdminFollowUpsRequest = z.infer<typeof SendHtsAdminFollowUpsRequestSchema>;

export const SubmitHtsAdminFollowUpAnswersRequestSchema = z.object({
  answers: z
    .array(
      z.object({
        questionId: z.string().min(1),
        answer: z.string().trim().min(1).max(MAX_ANSWER_MESSAGE_LENGTH),
      })
    )
    .max(MAX_ADMIN_FOLLOW_UPS),
});
export type SubmitHtsAdminFollowUpAnswersRequest = z.infer<typeof SubmitHtsAdminFollowUpAnswersRequestSchema>;
